import React, { useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useEvidenceModal } from "./EvidenceModalContext";

const GAP = 18;
const EDGE = 16;

const reducedMotion = () => window.matchMedia("(prefers-reduced-motion: reduce)").matches;

// Screen recording first, then the YouTube walkthrough, then the screenshot.
const Media = ({ project }) => {
  const youtube = project.videos?.[0];
  if (project.screenVideo) {
    return (
      <video
        className="project-tip-media"
        src={project.screenVideo}
        poster={project.screenImage}
        autoPlay={!reducedMotion()}
        muted
        loop
        playsInline
      />
    );
  }
  if (youtube) {
    return (
      <iframe
        className="project-tip-media"
        src={`https://www.youtube-nocookie.com/embed/${youtube.id}?autoplay=${reducedMotion() ? 0 : 1}&mute=1&rel=0&playsinline=1`}
        title={youtube.title}
        allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
        allowFullScreen
      />
    );
  }
  const image = project.device === "phone" ? project.screenImage : project.image;
  return image ? <img className="project-tip-media" src={image} alt="" /> : null;
};

export const ProjectTooltip = ({ project, detail, anchor, onClose }) => {
  const ref = useRef(null);
  const modal = useEvidenceModal();
  const [pos, setPos] = useState(null);
  const portrait = Boolean(project.screenVideo) || (!project.videos?.length && project.device === "phone");

  // Sit beside the device (right if it fits, else left), kept inside the viewport.
  useLayoutEffect(() => {
    const { width, height } = ref.current.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    let left = anchor.right + GAP;
    if (left + width > vw - EDGE) left = anchor.left - GAP - width;
    left = Math.max(EDGE, Math.min(left, vw - width - EDGE));
    const top = Math.max(EDGE, Math.min(anchor.top + anchor.height / 2 - height / 2, vh - height - EDGE));
    setPos({ left, top });
  }, [anchor]);

  const link = detail.links[0];

  return createPortal(
    <aside
      ref={ref}
      className={`project-tip ${portrait ? "project-tip--portrait" : "project-tip--landscape"}`}
      style={pos ?? { visibility: "hidden" }}
      role="dialog"
      aria-label={project.title}
    >
      <div className="project-tip-media-slot">
        <Media project={project} />
      </div>
      <div className="project-tip-body">
        <div className="project-tip-meta">
          <span>{detail.fileNo}</span>
          <span className="tag">{project.device === "phone" ? "mobile app" : project.imageKind === "document" ? "system" : "web app"}</span>
          {project.withTeam ? <span className="tag">team</span> : null}
        </div>
        <h3>{project.title}</h3>
        <p className="project-tip-role">{project.role}</p>
        <p className="project-tip-copy">{project.description}</p>
        <dl className="project-tip-facts">
          {detail.meta.slice(1).map((item) => (
            <div key={item.label}>
              <dt>{item.label}</dt>
              <dd>{item.value}</dd>
            </div>
          ))}
        </dl>
        <ul className="project-tip-stack">
          {project.stack.map((item) => (
            <li key={item} className="tag">{item}</li>
          ))}
        </ul>
        <div className="project-tip-foot">
          {link ? (
            <a
              href={link.href}
              className="exhibit-link"
              {...(link.internal ? {} : { target: "_blank", rel: "noreferrer" })}
            >
              {link.label} ↗
            </a>
          ) : null}
          <button
            type="button"
            className="project-tip-case"
            onClick={() => {
              onClose();
              modal.open(detail);
            }}
          >
            Open full case file
          </button>
        </div>
      </div>
    </aside>,
    document.body
  );
};

export default ProjectTooltip;
